<script lang="ts">
  // Drafts grouped by file, in path order and by line, each managed in
  // place: the Review view's list of unsent drafts.
  import type { ReviewDraft } from '../api/server';
  import type { PrStore } from '../state/pr.svelte';
  import { byFile, type Fate } from '../logic/inbox';
  import DraftCard from './DraftCard.svelte';

  interface Props {
    pr: PrStore;
    drafts: readonly ReviewDraft[];
    /** The draft under the keyboard cursor. */
    cursor?: ReviewDraft['id'] | null;
    /** The draft being edited in place. */
    editing?: ReviewDraft['id'] | null;
    /** What becomes of each draft at the next publish, where the page knows (the Review view). */
    fate?: ((id: ReviewDraft['id']) => Fate) | null;
    /** Why nothing in the list can be changed now, or null. */
    locked?: string | null;
    /** The drafts a publish is sending now. */
    sending?: ReadonlySet<ReviewDraft['id']> | null;
    onselect?: (d: ReviewDraft) => void;
    onjump?: (d: ReviewDraft) => void;
    ondiscard?: (d: ReviewDraft) => void;
    /** Where a draft's "Open in Files" leads. */
    hrefOf: (d: ReviewDraft) => string;
    /** A draft's include control was pressed. */
    oninclude?: (d: ReviewDraft) => void;
    /** A draft's text was saved: the write that saves it. */
    onedit?: (d: ReviewDraft, write: Promise<unknown>) => void;
  }
  let { pr, drafts, cursor = null, editing = $bindable(null), fate = null, locked = null, sending = null, hrefOf, onselect, onjump, ondiscard, oninclude, onedit }: Props = $props();

  const files = $derived(byFile(drafts));
</script>

<div class="draft-list">
  {#each files as f (f.path)}
    <section class="draft-file" aria-label={f.path}>
      <h3 class="draft-path mono">{f.path}<span class="muted"> · {f.drafts.length}</span></h3>
      {#each f.drafts as d (d.id)}
        <DraftCard
          {pr}
          draft={d}
          selected={cursor === d.id}
          fate={fate?.(d.id) ?? null}
          {locked}
          sending={sending?.has(d.id) ?? false}
          bind:editing={() => editing === d.id, (v) => (editing = v ? d.id : editing === d.id ? null : editing)}
          onselect={() => onselect?.(d)}
          onjump={() => onjump?.(d)}
          ondiscard={() => ondiscard?.(d)}
          href={hrefOf(d)}
          oninclude={() => oninclude?.(d)}
          onedit={(write) => onedit?.(d, write)}
        />
      {/each}
    </section>
  {/each}
</div>
